/**
 * Role-and-flag scoped rate-band visibility — the FE mirror of
 * api/_shared/bandVisibility.ts (same shape/behavior pair as
 * readRateBands: src/lib/notes.ts <-> api/_shared/rateBands.ts). Keep the
 * two in lockstep; the shared unit fixtures assert identical results.
 *
 * THE FOUNDER MATRIX (rule of record; Design Pass 4 §A moved the CM/SPICE
 * families off the role onto the two per-staff flags):
 *   - does_care_management flag (ANY role): care_management + travel.
 *   - admin_support (admin): admin ONLY.
 *   - does_spice flag (ANY role): spice + spice_friendly_visitor, NO travel.
 *   - owner, data_steward, exec_support (see-all): EVERY band, by role.
 *   - The flags are additive (UNION): a staffer with BOTH picks
 *     care_management, travel, spice, spice_friendly_visitor.
 *
 * The pickers (composer / pre-billing inline edit / notes feed) read the
 * actor's role + does_care_management + does_spice from whoami and filter
 * their band options through bandsForActor. The server enforces the same
 * set on write (a picker filter is UX; the server is the fence).
 */

import type { RateBandOption } from './notes';
import { readBandsOff } from './bandsOff';

/** A single band's visibility rule: the roles that may pick it, plus
 * whether the does_care_management / does_spice flags grant it (regardless
 * of role). */
export interface BandVisibilityRule {
  band: string;
  roles: readonly string[];
  care_management: boolean;
  spice: boolean;
}

/** The ordered default matrix, verbatim the founder ruling. */
export const DEFAULT_BAND_VISIBILITY: readonly BandVisibilityRule[] = [
  {
    band: 'care_management',
    roles: ['owner', 'data_steward', 'exec_support'],
    care_management: true,
    spice: false,
  },
  {
    band: 'travel',
    roles: ['owner', 'data_steward', 'exec_support'],
    care_management: true,
    spice: false,
  },
  {
    band: 'admin',
    roles: ['admin_support', 'owner', 'data_steward', 'exec_support'],
    care_management: false,
    spice: false,
  },
  {
    band: 'spice',
    roles: ['owner', 'data_steward', 'exec_support'],
    care_management: false,
    spice: true,
  },
  {
    band: 'spice_friendly_visitor',
    roles: ['owner', 'data_steward', 'exec_support'],
    care_management: false,
    spice: true,
  },
];

/** The actor dimensions the resolver keys on. */
export interface BandActor {
  role: string | null | undefined;
  does_care_management: boolean | null | undefined;
  does_spice: boolean | null | undefined;
}

/**
 * Read the tenant's band-visibility matrix from a tenants.config value
 * (config.billing.band_visibility). Defensive walk; falls back to
 * DEFAULT_BAND_VISIBILITY on anything malformed/empty. Pure; unit-tested.
 */
export function resolveBandVisibility(config: unknown): readonly BandVisibilityRule[] {
  if (typeof config !== 'object' || config === null) return DEFAULT_BAND_VISIBILITY;
  const billing = (config as Record<string, unknown>).billing;
  if (typeof billing !== 'object' || billing === null) return DEFAULT_BAND_VISIBILITY;
  const v = (billing as Record<string, unknown>).band_visibility;
  if (!Array.isArray(v)) return DEFAULT_BAND_VISIBILITY;
  const rules: BandVisibilityRule[] = [];
  for (const entry of v) {
    if (typeof entry !== 'object' || entry === null) continue;
    const band = (entry as Record<string, unknown>).band;
    const roles = (entry as Record<string, unknown>).roles;
    const careManagement = (entry as Record<string, unknown>).care_management;
    const spice = (entry as Record<string, unknown>).spice;
    if (typeof band !== 'string' || band.trim().length === 0) continue;
    if (!Array.isArray(roles)) continue;
    const cleanRoles: string[] = [];
    for (const r of roles) {
      if (typeof r === 'string' && r.trim().length > 0) cleanRoles.push(r.trim());
    }
    rules.push({
      band: band.trim(),
      roles: cleanRoles,
      care_management: careManagement === true,
      spice: spice === true,
    });
  }
  return rules.length > 0 ? rules : DEFAULT_BAND_VISIBILITY;
}

/**
 * The set of band VALUES this actor may select — additive union of
 * role-allowed and (when does_spice) spice-allowed, intersected with the
 * tenant vocabulary (result follows the vocabulary order). Pure;
 * heavily unit-tested.
 */
export function bandsForActor(
  actor: BandActor,
  matrix: readonly BandVisibilityRule[],
  allBands: readonly { value: string }[],
): string[] {
  const role = actor.role ?? '';
  const isCareManagement = actor.does_care_management === true;
  const isSpice = actor.does_spice === true;
  const allowed = new Set<string>();
  for (const rule of matrix) {
    const byRole = role !== '' && rule.roles.includes(role);
    const byCareManagement = isCareManagement && rule.care_management;
    const bySpice = isSpice && rule.spice;
    if (byRole || byCareManagement || bySpice) allowed.add(rule.band);
  }
  return allBands.map((b) => b.value).filter((v) => allowed.has(v));
}

/**
 * The actual RateBandOption list a picker should render for this actor —
 * bandsForActor mapped back to the {value,label} options (vocabulary
 * order preserved). The picker's convenience wrapper.
 */
export function bandOptionsForActor(
  actor: BandActor,
  matrix: readonly BandVisibilityRule[],
  allBands: readonly RateBandOption[],
): RateBandOption[] {
  const allowed = new Set(bandsForActor(actor, matrix, allBands));
  return allBands.filter((b) => allowed.has(b.value));
}

/**
 * The picker call sites' one-liner: scope the tenant's band options by the
 * signed-in user (whoami.role + whoami.does_spice) and the tenant's
 * config-resolved matrix. While whoami is still loading (null/undefined)
 * there is no actor to scope by, so the FULL vocabulary is offered rather
 * than a blank picker — the server-side guard is the fence, the picker
 * filter is UX.
 */
export function bandOptionsForWhoami(
  whoami:
    | { role?: string | null; does_care_management?: boolean; does_spice?: boolean }
    | null
    | undefined,
  config: unknown,
  allBands: readonly RateBandOption[],
  /** keep: a band the off filter lets through (an edit as a new version
   *  keeps its note's own band; the server's check has the same pass).
   *  The actor's matrix still applies to it. */
  opts: { keep?: string | null } = {},
): readonly RateBandOption[] {
  // A band switched off (billing.bands_off) is not offered, whoever asks,
  // except the note's own band when editing it.
  const kept = opts.keep ?? null;
  const off = readBandsOff(config);
  // Nothing off (or only the kept band): the vocabulary as it was given.
  const offered =
    [...off].every((v) => v === kept) ? allBands : allBands.filter((b) => !off.has(b.value) || b.value === kept);
  if (whoami === null || whoami === undefined) return offered;
  return bandOptionsForActor(
    {
      role: whoami.role,
      does_care_management: whoami.does_care_management ?? false,
      does_spice: whoami.does_spice ?? false,
    },
    resolveBandVisibility(config),
    offered,
  );
}

/** Single-value guard shape (mirrors the server's actorMayPickBand). */
export function actorMayPickBand(
  actor: BandActor,
  band: string,
  matrix: readonly BandVisibilityRule[],
  allBands: readonly { value: string }[],
): boolean {
  return bandsForActor(actor, matrix, allBands).includes(band);
}

/**
 * The two SPICE rate bands (mirrors api/_shared/bandVisibility.ts's
 * SPICE_NOTE_BANDS), fixed rather than tenant-configurable — the
 * platform band NAMES are stable regardless of which roles/flags may
 * pick them. Used to recognize a SPICE-band note (the "Session" chip,
 * NotesFeed.tsx) and to restrict a SPICE client's rate-band row
 * (NoteComposer.tsx) — neither reads the tenant-overridable matrix.
 */
export const SPICE_NOTE_BANDS: readonly string[] = DEFAULT_BAND_VISIBILITY.filter((r) => r.spice).map(
  (r) => r.band,
);

/** Whether a band value is one of the two SPICE bands. */
export function isSpiceBand(band: string): boolean {
  return SPICE_NOTE_BANDS.includes(band);
}

/** The office's client-billed band: engagement-neutral work (a rate on
 *  the client bills it), so every engagement offers it to an actor who
 *  holds it. */
export const ADMIN_RATE_BAND = 'admin';

/**
 * The rate-band row a client's engagement offers: a client whose
 * engagement is 'spice' offers the two SPICE bands and the admin band
 * (plus after-hours, unaffected by this list); 'both' and
 * 'care_management' offer the actor's full allowed set, unrestricted.
 * Applied on top of whatever bands the actor is otherwise allowed
 * (bandOptionsForWhoami) — a further restriction by the CLIENT's
 * engagement, not the actor's role, so a SPICE therapist never gains
 * the admin band here.
 */
export function bandOptionsForEngagement(
  engagement: 'care_management' | 'spice' | 'both' | undefined,
  allBands: readonly RateBandOption[],
): readonly RateBandOption[] {
  if (engagement !== 'spice') return allBands;
  // The SPICE family: the session pair plus a SPICE-family
  // per-visit band, so a SPICE-only client is offered its assessment.
  // The family is read off the band itself (a type-only import of notes.ts
  // keeps this module free of the browser runtime chain).
  return allBands.filter((b) => isSpiceBand(b.value) || b.family === 'spice' || b.value === ADMIN_RATE_BAND);
}
